import axios from 'axios'

const ADMIN_TOKEN_KEY = 'admin_token'
const ADMIN_REFRESH_KEY = 'admin_refresh_token'

function resolveApiBaseUrl() {
    const configuredBaseUrl = process.env.VUE_APP_API_BASE_URL?.trim()
    if (configuredBaseUrl) return configuredBaseUrl

    const hostname = window.location.hostname
    const protocol = window.location.protocol === 'http:' ? 'http:' : 'https:'
    const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1'

    if (isLocalHost) {
        return `${protocol}//localhost:8000/api/v1`
    }

    if (hostname.startsWith('admin.')) {
        return `${protocol}//api.${hostname.slice('admin.'.length)}/api/v1`
    }

    return `${protocol}//api.${hostname}/api/v1`
}

const API_BASE_URL = resolveApiBaseUrl()

export { ADMIN_TOKEN_KEY }

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
})

export function getAdminToken() {
    return sessionStorage.getItem(ADMIN_TOKEN_KEY) || ''
}

export function setAdminToken(access_token, refresh_token) {
    if (!access_token) {
        sessionStorage.removeItem(ADMIN_TOKEN_KEY)
        sessionStorage.removeItem(ADMIN_REFRESH_KEY)
        return
    }
    sessionStorage.setItem(ADMIN_TOKEN_KEY, access_token)
    if (refresh_token) sessionStorage.setItem(ADMIN_REFRESH_KEY, refresh_token)
}

export function clearAdminToken() {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY)
    sessionStorage.removeItem(ADMIN_REFRESH_KEY)
}

export function getApiBaseUrl() {
    return API_BASE_URL
}

/** @deprecated — does not add auth. Only use for non-file absolute URLs. */
export function getFullUrl(url) {
    if (!url) return ''
    if (url.startsWith('http')) return url
    const baseUrl = getApiBaseUrl().replace('/api/v1', '')
    return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`
}

/**
 * Exchange a stored /uploads/{filename} path for a short-lived signed URL.
 * Use this wherever you display an uploaded file (img src, video src, etc.).
 * Returns an empty string on error so the caller can show a fallback.
 */
export async function getAuthFileUrl(path) {
    if (!path) return ''
    if (path.startsWith('http') && !path.includes('/uploads/')) return path
    const filename = path.split('/').filter(Boolean).pop()
    if (!filename) return ''
    try {
        const res = await api.post(`/uploads/${filename}/token`)
        return res.data.signed_url
    } catch {
        return ''
    }
}

// ── Request interceptor : attach Bearer token ────────────────────────────
api.interceptors.request.use((config) => {
    const token = getAdminToken()
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
})

// ── Response interceptor : auto-refresh on 401 ───────────────────────────
let _refreshPromise = null

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config
        if (error.response?.status !== 401 || original._retried) {
            return Promise.reject(error)
        }
        const refreshToken = sessionStorage.getItem(ADMIN_REFRESH_KEY)
        if (!refreshToken) {
            clearAdminToken()
            window.dispatchEvent(new CustomEvent('admin:session-expired'))
            return Promise.reject(error)
        }
        // Deduplicate concurrent refresh calls
        if (!_refreshPromise) {
            _refreshPromise = axios
                .post(`${API_BASE_URL}/auth/refresh`, { refresh_token: refreshToken })
                .then((res) => {
                    setAdminToken(res.data.access_token, res.data.refresh_token)
                    return res.data.access_token
                })
                .catch(() => {
                    clearAdminToken()
                    window.dispatchEvent(new CustomEvent('admin:session-expired'))
                    return null
                })
                .finally(() => { _refreshPromise = null })
        }
        const newToken = await _refreshPromise
        if (!newToken) return Promise.reject(error)
        original._retried = true
        original.headers.Authorization = `Bearer ${newToken}`
        return api(original)
    }
)

export function extractApiError(error) {
    const detail = error?.response?.data?.detail
    if (typeof detail === 'string' && detail.trim()) return detail
    if (Array.isArray(detail) && detail.length > 0) return JSON.stringify(detail)
    if (typeof error?.message === 'string' && error.message.trim()) return error.message
    return 'Erreur reseau/API'
}

export default api
